import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { api } from '../lib/api';
import { TaskRow } from './TaskRow';
import { TaskEditor } from './TaskEditor';
import { pushToast } from '../lib/toast';
import { useRouter } from '../lib/router';

export function ProjectView({ projectId }: { projectId: string }) {
  const project = useQuery({ queryKey: ['project', projectId], queryFn: () => api.listProjects().then((ps) => ps.find((p) => p.id === projectId)) });
  const tasks = useQuery({ queryKey: ['tasks', { projectId }], queryFn: () => api.listTasks({ projectId }) });
  const areas = useQuery({ queryKey: ['areas'], queryFn: api.listAreas });
  const qc = useQueryClient();
  const router = useRouter();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState('');

  const create = useMutation({
    mutationFn: (title: string) => api.createTask({ title, projectId, status: 'next' } as any),
    onSuccess: () => {
      pushToast('Added.', 'success');
      setNewTitle('');
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['view'] });
    },
  });

  const updateProject = useMutation({
    mutationFn: (patch: any) => api.updateProject(projectId, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project', projectId] }),
  });

  const deleteProject = useMutation({
    mutationFn: () => api.deleteProject(projectId),
    onSuccess: () => {
      pushToast('Project deleted. Onwards.');
      router.navigate('/projects');
      qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });

  if (!project.data) {
    return <div className="p-6 text-zinc-500">Loading…</div>;
  }
  const p = project.data;
  const ts = tasks.data ?? [];
  const open = ts.filter((t) => t.status !== 'done');
  const done = ts.filter((t) => t.status === 'done');

  return (
    <div className="mx-auto max-w-3xl px-3 md:px-6 py-4 md:py-8">
      <div className="flex items-start justify-between gap-2 mb-2">
        <input
          className="bg-transparent text-2xl md:text-3xl font-semibold tracking-tight outline-none flex-1"
          defaultValue={p.name}
          onBlur={(e) => e.target.value.trim() && updateProject.mutate({ name: e.target.value.trim() })}
        />
        <button
          onClick={() => {
            if (confirm('Delete this project? Tasks are kept, but unparented.')) deleteProject.mutate();
          }}
          className="p-2 text-red-400 hover:bg-zinc-800 rounded-lg"
          aria-label="Delete project"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <div className="flex items-center gap-2 mb-4 text-sm text-zinc-500 flex-wrap">
        <select
          value={p.status}
          onChange={(e) => updateProject.mutate({ status: e.target.value })}
          className="bg-zinc-950 border border-zinc-800 rounded-md px-2 py-1"
        >
          {['active', 'paused', 'someday', 'done'].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={p.areaId ?? ''}
          onChange={(e) => updateProject.mutate({ areaId: e.target.value || null })}
          className="bg-zinc-950 border border-zinc-800 rounded-md px-2 py-1"
        >
          <option value="">No area</option>
          {(areas.data ?? []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </div>

      <textarea
        defaultValue={p.notes ?? ''}
        placeholder="Brief description, outcome, success criteria…"
        className="w-full bg-zinc-900/40 border border-zinc-800 rounded-lg p-3 text-sm mb-5 min-h-[80px] outline-none focus:border-amber-500"
        onBlur={(e) => updateProject.mutate({ notes: e.target.value })}
      />

      <form
        className="mb-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (newTitle.trim()) create.mutate(newTitle.trim());
        }}
      >
        <input
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          placeholder="+ Add a task to this project"
          className="w-full bg-zinc-900/40 border border-zinc-800 rounded-lg px-3 py-2 text-sm outline-none focus:border-amber-500"
        />
      </form>

      <ul className="divide-y divide-zinc-800/70 rounded-xl border border-zinc-800 bg-zinc-900/40 overflow-hidden">
        {open.map((t) => (
          <TaskRow key={t.id} task={t as any} onOpen={() => setEditingId(t.id)} />
        ))}
      </ul>

      {done.length > 0 && (
        <details className="mt-6">
          <summary className="text-sm text-zinc-500 cursor-pointer">Completed ({done.length})</summary>
          <ul className="mt-2 divide-y divide-zinc-800/70 rounded-xl border border-zinc-800 bg-zinc-900/40 overflow-hidden">
            {done.map((t) => (
              <TaskRow key={t.id} task={t as any} onOpen={() => setEditingId(t.id)} />
            ))}
          </ul>
        </details>
      )}

      {editingId && <TaskEditor taskId={editingId} onClose={() => setEditingId(null)} />}
    </div>
  );
}
